// Geolocation service for detecting user's country using IP address (no GPS/location service required)

// Top 30+ countries by internet users (2024 data)
export const TOP_20_COUNTRIES = [
  { code: 'IN', name: 'India', flag: '🇮🇳', internetUsers: 692000000 },
  { code: 'SG', name: 'Singapore', flag: '🇸🇬', internetUsers: 5000000 },
  { code: 'MY', name: 'Malaysia', flag: '🇲🇾', internetUsers: 29000000 },
  { code: 'CN', name: 'China', flag: '🇨🇳', internetUsers: 1050000000 },
  { code: 'US', name: 'United States', flag: '🇺🇸', internetUsers: 311000000 },
  { code: 'ID', name: 'Indonesia', flag: '🇮🇩', internetUsers: 212000000 },
  { code: 'BR', name: 'Brazil', flag: '🇧🇷', internetUsers: 165000000 },
  { code: 'PK', name: 'Pakistan', flag: '🇵🇰', internetUsers: 111000000 },
  { code: 'NG', name: 'Nigeria', flag: '🇳🇬', internetUsers: 109000000 },
  { code: 'BD', name: 'Bangladesh', flag: '🇧🇩', internetUsers: 99000000 },
  { code: 'RU', name: 'Russia', flag: '🇷🇺', internetUsers: 129000000 },
  { code: 'MX', name: 'Mexico', flag: '🇲🇽', internetUsers: 96000000 },
  { code: 'JP', name: 'Japan', flag: '🇯🇵', internetUsers: 118000000 },
  { code: 'PH', name: 'Philippines', flag: '🇵🇭', internetUsers: 89000000 },
  { code: 'VN', name: 'Vietnam', flag: '🇻🇳', internetUsers: 72000000 },
  { code: 'TR', name: 'Turkey', flag: '🇹🇷', internetUsers: 69000000 },
  { code: 'DE', name: 'Germany', flag: '🇩🇪', internetUsers: 79000000 },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧', internetUsers: 65000000 },
  { code: 'FR', name: 'France', flag: '🇫🇷', internetUsers: 60000000 },
  { code: 'TH', name: 'Thailand', flag: '🇹🇭', internetUsers: 57000000 },
  { code: 'IT', name: 'Italy', flag: '🇮🇹', internetUsers: 61000000 },
  { code: 'KR', name: 'South Korea', flag: '🇰🇷', internetUsers: 50000000 },
  { code: 'EG', name: 'Egypt', flag: '🇪🇬', internetUsers: 75000000 },
  { code: 'IR', name: 'Iran', flag: '🇮🇷', internetUsers: 70000000 },
  { code: 'ES', name: 'Spain', flag: '🇪🇸', internetUsers: 45000000 },
  { code: 'AR', name: 'Argentina', flag: '🇦🇷', internetUsers: 40000000 },
  { code: 'CO', name: 'Colombia', flag: '🇨🇴', internetUsers: 38000000 },
  { code: 'CA', name: 'Canada', flag: '🇨🇦', internetUsers: 35000000 },
  { code: 'PL', name: 'Poland', flag: '🇵🇱', internetUsers: 32000000 },
  { code: 'SA', name: 'Saudi Arabia', flag: '🇸🇦', internetUsers: 33000000 },
  { code: 'UA', name: 'Ukraine', flag: '🇺🇦', internetUsers: 30000000 },
  { code: 'AU', name: 'Australia', flag: '🇦🇺', internetUsers: 25000000 },
];

/**
 * Detects user's country using IP-based geolocation
 * No GPS or location services required - works entirely through IP address
 */
export async function detectUserCountry(): Promise<string> {
  try {
    console.log('Detecting country from IP address...');
    
    // Check localStorage cache first (client-side only)
    if (typeof window !== 'undefined') {
      const cachedCountry = localStorage.getItem('user_country');
      const cacheTimestamp = localStorage.getItem('user_country_timestamp');
      
      if (cachedCountry && cacheTimestamp) {
        const cacheAge = Date.now() - parseInt(cacheTimestamp);
        // Cache valid for 24 hours
        if (cacheAge < 24 * 60 * 60 * 1000) {
          console.log(`Using cached country: ${cachedCountry}`);
          return cachedCountry;
        }
      }
    }
    
    // Use ipapi.co for IP-based geolocation (no location permission needed)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
    
    const response = await fetch('https://ipapi.co/json/', {
      cache: 'no-store',
      signal: controller.signal,
    });
    
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      console.log(`IP geolocation API returned status ${response.status}, using default country`);
      return 'IN';
    }
    
    const data = await response.json();
    
    if (data.country_code) {
      const countryCode = data.country_code.toUpperCase();
      console.log(`Detected country from IP: ${countryCode} (${data.country_name})`);
      
      // Check if detected country is in our supported list
      const isSupported = TOP_20_COUNTRIES.some(c => c.code === countryCode);
      
      const finalCountry = isSupported ? countryCode : 'IN';
      
      // Cache the result (client-side only)
      if (typeof window !== 'undefined') {
        localStorage.setItem('user_country', finalCountry);
        localStorage.setItem('user_country_timestamp', Date.now().toString());
      }
      
      if (!isSupported) {
        console.log(`Country ${countryCode} not in supported list, defaulting to India`);
      }
      
      return finalCountry;
    }
    
    console.log('No country code in response, using default country');
    return 'IN';
  } catch (error) {
    // Silently handle errors and use default country
    // This is expected behavior when geolocation services are unavailable
    if (error instanceof Error && error.name === 'AbortError') {
      console.log('IP geolocation request timed out, using default country');
    } else {
      console.log('IP geolocation unavailable, using default country');
    }
    return 'IN';
  }
}

export function getCountryName(countryCode: string): string {
  const country = TOP_20_COUNTRIES.find(c => c.code === countryCode);
  return country?.name || 'Unknown';
}

export function getCountryFlag(countryCode: string): string {
  const country = TOP_20_COUNTRIES.find(c => c.code === countryCode);
  return country?.flag || '🌍';
}